import { useEffect, useMemo, useState } from "react";
import {
  COZULDU_ICIN_ARDISIK_DOGRU,
  HATA_TIPI_SIRASI,
  kapanmayaKalanMetni,
} from "@koclukpanel/contracts";
import { Ikon, MetinAlani, Secim, cn, type IkonAdi } from "@koclukpanel/ui";
import {
  ApiHatasi,
  hataDefterim,
  hataEkle,
  hataKaldir,
  hataTekrar,
  hataTipleri,
  kendiKaydim,
  konularim,
  type HataKaydi,
  type HataTipi,
  type KonumKonu,
} from "./api";

/**
 * HATALI SORU DEFTERİM — F-MST
 *
 * ================================================================
 * KAYDI ÖĞRENCİ AÇAR
 * ================================================================
 * Şartnamedeki iş bölümü: yanlış yapılan soruyu öğrenci kaydeder
 * (F-MST-01), uzman hata tipini düzeltir ve açıklama ekler
 * (F-MST-03). Bu ekran kayıt açma ve tekrar çözme tarafı.
 *
 * ================================================================
 * TEK DOKUNUŞLA KAYIT
 * ================================================================
 * Soru çözerken açılan bir ekran bu: uzun form doldurtmak, öğrencinin
 * hiç kaydetmemesine yol açar. Zorunlu tek alan KONU; hata tipi ve
 * not isteğe bağlı ve sonradan da doldurulabiliyor.
 *
 * Konu seçimi ders ADIYLA gruplanıyor ama gönderilen şey `lessonId`
 * ve `topicId` — ekran ad üzerinden eşleştirme yapmıyor.
 */

/*
  ================================================================
  SIRA SÖZLEŞMEDEN — ETİKETLER BURADA KALIYOR
  ================================================================
  Burada aynı dizi elle yazılıydı ve sözleşmedeki `HATA_TIPI_SIRASI`
  ile birebir aynıydı. Sözleşme sıranın ANLAMLI olduğunu açıkça
  söylüyor: önce öğrenmeyle ilgili olanlar, sonra dikkatle ilgili
  olanlar, en sonda sınav yönetimiyle ilgili olan (süre). Uzman ısı
  haritasını soldan sağa okurken "çalışma sorunu mu, sınav sorunu mu"
  ayrımını bu sıradan görüyor (F-MST-07) ve panel sırayı zaten
  sözleşmeden alıyor.

  İki taraf sırayı ayrı ayrı yazsaydı, biri değiştiği gün ikisi
  ayrışır ve KARŞILAŞTIRMA sessizce yanlış olurdu — hiçbir şey
  patlamaz, yalnızca çıkarılan sonuç yanlış olur.

  ETİKETLER (`TIP_ACIKLAMASI`) BİLEREK BURADA: öğrenci "Konuyu
  bilmiyordum" görüyor, uzman "Bilgi eksiği". Aynı kavram, farklı
  muhatap, farklı dil. Bu bir kopya değil, doğru bir ayrım —
  sözleşmeye taşımak iki hedef kitlenin metnini tek yere yığardı.
*/
const TIP_SIRASI: readonly HataTipi[] = HATA_TIPI_SIRASI;

/**
 * Her tipin kendi cizgisi.
 *
 * Bes secenek YAN YANA duruyor; sozlugun "ayni ikon iki kavrama
 * verilmez" kurali en cok burada goze carpiyor ve `Ikon.test.tsx`
 * bunu olcuyor.
 */
const TIP_IKONU: Record<HataTipi, IkonAdi> = {
  KNOWLEDGE_GAP: "bilgiEksigi",
  CARELESS: "dikkatsizlik",
  MISREAD: "yanlisOkuma",
  CALCULATION: "islemHatasi",
  TIME: "sureYetmedi",
};

/**
 * Alt metinler CIZIMDEN BIREBIR.
 *
 * Baslik sunucudan geliyor (`hataTipleri()`) ve tek basina soyut
 * kaliyor: "Dikkatsizlik" ogrenciye kendi durumunu tarif etmiyor.
 * Alt satir onun cumlesi — "Biliyordum ama yanlis isaretledim".
 * Bu metinler ekranin isini degistiriyor: ogrenci dogru tipi
 * secmezse uzman yanlis dersi cikarir.
 */
const TIP_ACIKLAMASI: Record<HataTipi, string> = {
  KNOWLEDGE_GAP: "Konuyu bilmiyordum",
  CARELESS: "Biliyordum ama yanlış işaretledim",
  MISREAD: "Soruyu yanlış anladım",
  CALCULATION: "Yolu doğruydu, hesapta hata",
  TIME: "Çözemeden zaman doldu",
};

function gun(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR");
}

export function HataDefterim() {
  /*
    KENDI KAYIT KIMLIGINI EKRAN ISTIYOR, ust bilesenden GECMIYOR.
    Uygulama.tsx zaten her ekranin kendi verisini cektigi bir yapida;
    kimligi yukaridan gecirmek, ekrani cagiranin sirasina bagimli
    kilardi (kimlik daha gelmemisken ekran acilirsa bos kalirdi).
  */
  const [ogrenciId, setOgrenciId] = useState<string | null>(null);
  const [kayitlar, setKayitlar] = useState<HataKaydi[] | null>(null);
  const [konular, setKonular] = useState<KonumKonu[]>([]);
  const [tipler, setTipler] = useState<Record<string, string>>({});
  const [hata, setHata] = useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [islemde, setIslemde] = useState(false);
  const [formAcik, setFormAcik] = useState(false);
  const [yalnizcaCozulmemis, setYalnizca] = useState(true);

  const [konuId, setKonuId] = useState("");
  const [tip, setTip] = useState<string>("");
  const [not, setNot] = useState("");

  const yukle = async (cozulmemis: boolean): Promise<void> => {
    setYukleniyor(true);
    setHata(null);
    try {
      const id = ogrenciId ?? (await kendiKaydim()).id;
      if (ogrenciId === null) setOgrenciId(id);
      const [k, ko, t] = await Promise.all([
        hataDefterim(id, cozulmemis),
        konular.length > 0 ? Promise.resolve(konular) : konularim(id),
        Object.keys(tipler).length > 0 ? Promise.resolve(tipler) : hataTipleri(),
      ]);
      setKayitlar(k);
      setKonular(ko);
      setTipler(t);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : "Defter alınamadı.");
    } finally {
      setYukleniyor(false);
    }
  };

  /*
    Yalnizca SUZGEC degisince yeniden yukleniyor. `yukle` bagimlilik
    listesine konsaydi her cizimde yeni bir fonksiyon olusur ve sonsuz
    dongu uretirdi; islev zaten kendi kimligini icerde cozuyor.
  */
  useEffect(() => {
    void yukle(yalnizcaCozulmemis);
  }, [yalnizcaCozulmemis]);

  /*
    Ders adına göre grupluyoruz ama SEÇİLEN DEĞER `topicId`. Ders adı
    yalnızca okumayı kolaylaştırıyor; eşleştirme kimlikle yapılıyor.
  */
  const grupli = useMemo(() => {
    const m = new Map<string, KonumKonu[]>();
    /*
      ARŞİVLİ KONU BURADA SUNULMUYOR — 07.09.2026.
      Liste ilerleme ekranıyla ortak ve orada arşivli konu GÖRÜNMELİ
      (öğrenci onu çalışmış olabilir). Ama yeni bir kayıtta seçilirse
      sunucu reddediyor; seçtirip reddetmek, hiç seçtirmemekten kötü.
      Canlı denemede ölçüldü: liste arşivlenmiş bir konuyu sunuyordu.
    */
    for (const k of konular.filter((x) => !x.arsivli)) {
      const l = m.get(k.ders) ?? [];
      l.push(k);
      m.set(k.ders, l);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], "tr"));
  }, [konular]);

  const kaydet = async (): Promise<void> => {
    const konu = konular.find((k) => k.topicId === konuId);
    if (!konu || ogrenciId === null) return;
    setIslemde(true);
    setHata(null);
    try {
      await hataEkle({
        ogrenciId,
        lessonId: konu.lessonId,
        topicId: konu.topicId,
        errorType: (tip || null) as HataTipi | null,
        studentNote: not.trim() || null,
      });
      setKonuId("");
      setTip("");
      setNot("");
      setFormAcik(false);
      await yukle(yalnizcaCozulmemis);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : "Kaydedilemedi.");
    } finally {
      setIslemde(false);
    }
  };

  const tekrarBildir = async (id: string, dogruMu: boolean): Promise<void> => {
    setIslemde(true);
    setHata(null);
    try {
      await hataTekrar(id, dogruMu);
      await yukle(yalnizcaCozulmemis);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : "İşlem tamamlanamadı.");
    } finally {
      setIslemde(false);
    }
  };

  const kaldir = async (id: string): Promise<void> => {
    setIslemde(true);
    setHata(null);
    try {
      await hataKaldir(id);
      await yukle(yalnizcaCozulmemis);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : "Kaldırılamadı.");
    } finally {
      setIslemde(false);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-notr-900 text-base font-semibold">Hata defterim</h2>
        <button
          type="button"
          onClick={() => setFormAcik((a) => !a)}
          aria-expanded={formAcik}
          className="min-h-dokunma rounded-alan text-notr-0 ease-cikis focus-visible:outline-marka-600 bg-(image:--gradyan-birincil) px-4 text-base font-medium shadow-(--golge-birincil) transition-[box-shadow,translate] duration-(--sure-hizli) focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 enabled:hover:-translate-y-px enabled:hover:shadow-(--golge-birincil-uzerinde) enabled:active:translate-y-0 enabled:active:shadow-(--golge-birincil-basili)"
        >
          {formAcik ? "Kapat" : "Yanlış soru ekle"}
        </button>
      </div>

      {hata && (
        <p role="alert" className="rounded-alan bg-hata-500/10 text-hata-700 p-3 text-base">
          {hata}
        </p>
      )}

      {formAcik && (
        <div className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4">
          <div className="flex flex-col gap-1.5">
            <Secim etiket="Hangi konu" value={konuId} onChange={(e) => setKonuId(e.target.value)}>
              <option value="">Seç…</option>
              {grupli.map(([ders, liste]) => (
                <optgroup key={ders} label={ders}>
                  {liste.map((k) => (
                    <option key={k.topicId} value={k.topicId}>
                      {k.ad}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Secim>
            {konular.length === 0 && !yukleniyor && (
              /*
                MÜFREDAT ATANMAMIŞSA kayıt açılamıyor — ve sebebi
                söyleniyor. "Seç…" yazan boş bir liste, öğrenciye
                kendi hatası gibi görünürdü.
              */
              <p className="text-notr-600 text-sm">
                Müfredatınız henüz atanmamış; konu seçilemiyor. Uzmanınıza söyleyin.
              </p>
            )}
          </div>

          {/*
            ================================================================
            BEŞ TİP, TEK DOKUNUŞ — AÇILIR LİSTE DEĞİL
            ================================================================
            Çizim paketi bunu açıkça reddediyor ve gerekçesini yazıyor:
            "Açılır liste DEĞİL: bu ekranın en sık yapılan işi bu ve her
            yanlış için tekrarlanıyor."

            Ölçü: bir `<select>` telefonda üç dokunuş istiyor (aç, kaydır,
            seç) ve her yanlış soru için tekrarlanıyor. Beş seçenek zaten
            ekrana sığıyorken listeye gizlemek, en sık yapılan işi en
            pahalı iş yapıyordu.

            `radiogroup` olarak duyuruluyor: görsel olarak düğme ama
            anlamı "birini seç". Ekran okuyucu kullanıcısı kaç seçenek
            olduğunu ve hangisinde olduğunu duyuyor.
          */}
          <fieldset className="flex flex-col gap-2.5">
            <legend className="text-notr-900 pb-1 text-[15px] font-semibold">
              Neden yanlış yaptın?
            </legend>
            <div className="flex flex-col gap-2" role="radiogroup" aria-label="Yanlışın sebebi">
              {TIP_SIRASI.filter((t) => t in tipler).map((t) => {
                const secili = tip === t;
                return (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={secili}
                    /* Aynı düğmeye tekrar basmak seçimi KALDIRIYOR: alan
                       isteğe bağlı ve "Bilmiyorum / sonra" seçeneğini
                       ayrı bir satır olarak çizmek listeyi altıya
                       çıkarırdı. */
                    onClick={() => setTip(secili ? "" : t)}
                    className={cn(
                      "min-h-dokunma rounded-kart flex items-center gap-3 px-[15px] py-[13px] text-left",
                      "ease-cikis transition-[background-color,box-shadow] duration-(--sure-anlik)",
                      "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                      secili
                        ? "bg-marka-50 shadow-[inset_0_0_0_2px_var(--color-marka-600)]"
                        : "bg-notr-0 hover:bg-notr-50 shadow-[inset_0_0_0_1px_var(--color-notr-200)]",
                    )}
                  >
                    <Ikon
                      ad={TIP_IKONU[t]}
                      boyut="normal"
                      className={secili ? "text-marka-700" : "text-notr-600"}
                    />
                    <span className="flex flex-col gap-px">
                      <span
                        className={cn(
                          "text-[15px]",
                          secili ? "text-marka-700 font-semibold" : "text-notr-900 font-medium",
                        )}
                      >
                        {tipler[t]}
                      </span>
                      {/* Alt metin çizimden birebir: başlık tek başına
                          "dikkatsizlik" gibi soyut kalıyor, alt satır
                          öğrencinin kendi cümlesini veriyor. */}
                      <span
                        className={cn("text-[13px]", secili ? "text-notr-600" : "text-notr-600")}
                      >
                        {TIP_ACIKLAMASI[t]}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          <MetinAlani
            etiket="Notun (isteğe bağlı)"
            rows={3}
            value={not}
            onChange={(e) => setNot(e.target.value)}
            yardim="Soruyu hatırlatacak birkaç kelime yeter. Uzmanın bunu görecek."
          />

          <button
            type="button"
            disabled={!konuId || islemde}
            onClick={() => void kaydet()}
            className="min-h-dokunma rounded-alan text-notr-0 ease-cikis disabled:bg-notr-200 disabled:text-notr-600 focus-visible:outline-marka-600 bg-(image:--gradyan-birincil) px-4 text-base font-medium shadow-(--golge-birincil) transition-[box-shadow,translate] duration-(--sure-hizli) focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 enabled:hover:-translate-y-px enabled:hover:shadow-(--golge-birincil-uzerinde) enabled:active:translate-y-0 enabled:active:shadow-(--golge-birincil-basili) disabled:bg-none disabled:shadow-none"
          >
            {islemde ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      )}

      <label className="min-h-dokunma flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          className="accent-marka-600 size-5"
          checked={yalnizcaCozulmemis}
          onChange={(e) => setYalnizca(e.target.checked)}
        />
        <span className="text-notr-800 text-base">Yalnızca çözemediklerim</span>
      </label>

      {yukleniyor ? (
        <p className="text-notr-600 text-base">Yükleniyor…</p>
      ) : (kayitlar?.length ?? 0) === 0 ? (
        <p className="rounded-kart border-notr-200 bg-notr-0 shadow-kart text-notr-700 border p-4 text-base">
          {yalnizcaCozulmemis
            ? "Çözemediğin soru kalmamış."
            : "Defterin boş. Yanlış yaptığın bir soruyu buraya ekleyince, sonra dönüp tekrar çözebilirsin."}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {(kayitlar ?? []).map((k) => (
            <li
              key={k.id}
              className="rounded-kart border-notr-200 bg-notr-0 shadow-kart border p-4"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-notr-900 text-base font-medium">
                  {k.lesson?.name ?? "—"}
                  {k.topic?.name ? ` · ${k.topic.name}` : ""}
                </span>
                {k.errorType && (
                  <span className="bg-notr-100 text-notr-700 rounded-full px-2 py-0.5 text-sm">
                    {tipler[k.errorType] ?? k.errorType}
                  </span>
                )}
                <span className="text-notr-600 text-sm">{gun(k.createdAt)}</span>
              </div>

              {k.studentNote && (
                <p className="text-notr-700 mt-1 text-base whitespace-pre-wrap">{k.studentNote}</p>
              )}

              {k.coachNote && (
                /*
                  UZMAN AÇIKLAMASI AYRI KUTUDA: öğrencinin kendi
                  notuyla karışmamalı — biri soruyu hatırlatmak için,
                  öteki neyi yanlış anladığını anlatmak için.
                */
                <div className="rounded-alan bg-marka-50 mt-2 p-3">
                  <p className="text-notr-600 text-sm font-medium">Uzmanının açıklaması</p>
                  <p className="text-notr-800 text-base whitespace-pre-wrap">{k.coachNote}</p>
                </div>
              )}

              {k.resolvedAt ? (
                <p className="text-onay-700 mt-2 text-base font-medium">Çözüldü</p>
              ) : (
                <div className="mt-3 flex flex-col gap-2">
                  {/*
                    ================================================================
                    KAPANMA CÜMLESİ SÖZLEŞMEDEN — KOPYASI SÜRÜKLENİYORDU
                    ================================================================
                    Burada "Bir kez daha doğru çözersen kapanacak." elle
                    yazılıydı ve bu, sunucudaki `tekrarCozuldu` kuralının
                    (üst üste ikinci doğru) bir KOPYASIYDI. Kural üçe
                    çıkarılsaydı ekran yalan söylerdi ve **istemci testi
                    bunu göremezdi** — çünkü test de aynı cümleyi
                    doğrular. Aynı sınıf `haftaBasi`da 04.09.2026'da
                    gerçekten oldu (C14.1).

                    Sayı ve cümle artık `@koclukpanel/contracts` içinde;
                    kural değiştiğinde metin de değişiyor.
                  */}
                  <p className="text-notr-600 text-sm">
                    {k.lastRetryOk === true
                      ? `Bir kez doğru çözdün. ${kapanmayaKalanMetni(COZULDU_ICIN_ARDISIK_DOGRU - 1)}`
                      : "Tekrar çöz ve sonucu işaretle."}
                  </p>
                  {/*
                    ================================================================
                    KENDİ DENEME SAYISI — KOÇ GÖRÜYORDU, ÖĞRENCİ GÖRMÜYORDU
                    ================================================================
                    Ölçüldü (05.09.2026, C14.32): `retryCount` telde
                    geliyor ve panelde koça "3 deneme" diye yazılıyor
                    (`HataDefteri.tsx:249`); öğrencinin kendi defterinde
                    hiçbir yerde yoktu. Kendi çabasını gösteren sayı,
                    çabayı gösterene değil İZLEYENE gösteriliyordu.

                    CÜMLE PANELDEN FARKLI ve bilerek: koça "3 deneme"
                    bir ÖLÇÜM, öğrenciye aynı sayı bir YARGI gibi
                    okunabilir ("üç kez yanlış yaptın"). "Denedin"
                    fiili çabayı özneye veriyor.

                    SIFIRDA HİÇ YAZILMIYOR: "0 kez denedin" bir bilgi
                    değil, boş bir satır — ve henüz denememiş öğrenciye
                    denemediğini söylemek gereksiz.
                  */}
                  {k.retryCount > 0 && (
                    <p className="text-notr-600 text-xs">{k.retryCount} kez denedin.</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={islemde}
                      onClick={() => void tekrarBildir(k.id, true)}
                      className="min-h-dokunma rounded-alan text-notr-0 ease-cikis disabled:bg-notr-200 disabled:text-notr-600 focus-visible:outline-marka-600 bg-(image:--gradyan-birincil) px-4 text-base font-medium shadow-(--golge-birincil) transition-[box-shadow,translate] duration-(--sure-hizli) focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 enabled:hover:-translate-y-px enabled:hover:shadow-(--golge-birincil-uzerinde) enabled:active:translate-y-0 enabled:active:shadow-(--golge-birincil-basili) disabled:bg-none disabled:shadow-none"
                    >
                      Doğru çözdüm
                    </button>
                    <button
                      type="button"
                      disabled={islemde}
                      onClick={() => void tekrarBildir(k.id, false)}
                      className="min-h-dokunma rounded-alan bg-notr-100 text-notr-700 focus-visible:outline-marka-600 px-4 text-base font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50"
                    >
                      Yine yanlış
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-2">
                <button
                  type="button"
                  disabled={islemde}
                  onClick={() => void kaldir(k.id)}
                  className="min-h-dokunma text-notr-600 focus-visible:outline-marka-600 text-base underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50"
                >
                  Defterimden kaldır
                  <span className="sr-only"> — {k.topic?.name ?? k.lesson?.name ?? "kayıt"}</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
